/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { createTheme,ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Material Dashboard 2 React routes
import routes from "routes";
// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

import TagManager from 'react-gtm-module';


export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  useEffect(() => {
    const tagManagerArgs = {
      gtmId: 'GTM-MD8JP4XV'
    };
    TagManager.initialize(tagManagerArgs);
  }, []);

  // Function to get the user object from localStorage
  const getUserFromLocalStorage = () => {
    const storedUser = localStorage.getItem("user");
    
    // Check if user exists in localStorage
    if (storedUser) {
      try {
        // Parse the stored user object
        const userObject = JSON.parse(storedUser).user;

        // Check if user object exists and has first_name and last_name
        if (userObject && userObject.first_name && userObject.last_name) {
          return userObject;
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    return null;
  };

  // Get the user object from localStorage
  const userObject = getUserFromLocalStorage(); 
  
  // Ensure userObject is defined and IsAdminPlatform exists, default to false if not
  const isAdminPlatform = userObject?.IsAdminPlatform ?? false;

  // console.log(userObject);

  // Declare filteredRoutes outside of if-else blocks
  let filteredRoutes;


  if (isAdminPlatform) {

    // Dynamically filter routes based on user's role or authentication status
    filteredRoutes = routes.filter(route => {
      // If the route has a requiredRole, filter based on the user's role
      if (route.requiredRole == 'user' || route.requiredRole == 'suser') {
        return false; //  Not show route if the user's role matches
      }
      return true; // Show route if no role restriction
    });


  } else {

    // The array of keys you want to search for
    const searchKeys = ["dashboard", "/dashboard/request-list","dashboard/Elenco-Richieste", "dashboard/request-list", "authentication/log-out","dashboard/change-password"];
    
    // Filter the array to match any of the search keys
    const filteredItems = routes.filter(item => searchKeys.includes(item.key));
    // console.log(filteredItems);
    // Output the result
    // console.log(filteredItems);

    // Dynamically filter routes based on user's role or authentication status
    filteredRoutes = filteredItems.filter(route => {
      // If the route has a requiredRole, filter based on the user's role
      if (route.requiredRole == 'user') {
        return false; //  Not show route if the user's role matches
      }
      return true; // Show route if no role restriction
    });


  }

  
  // Dynamically filter routes based on user's role or authentication status
  // const filteredRoutes = routes.filter(route => {
  //   // If the route has a requiredRole, filter based on the user's role
  //   if (route.requiredRole == 'user') {
  //     return false; //  Not show route if the user's role matches
  //   }
  //   return true; // Show route if no role restriction
  // });


  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
    console.log(direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });


  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      // onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Material Dashboard 2"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) :  // left-to-right
   (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            // brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName="ShareITalents"
            routes={filteredRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
            
          />
          {/* <Configurator /> */}
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        {getRoutes(routes)}
        {/* <Route path="*" element={<Navigate to="/dashboard" />} /> */}
        <Route path="*" element={<Navigate to="/home" />} />
        
      </Routes>
    </ThemeProvider>
  );
}
